import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Input, Button, Radio, Modal, message, Divider, Empty, Cascader } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { cartAPI, addressAPI, orderAPI, regionsAPI } from '../services/api';
import { requireAuth } from '../utils/auth';
import './Checkout.css';

function Checkout() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [cartItems, setCartItems] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [regions, setRegions] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [selectedRegionOptions, setSelectedRegionOptions] = useState([]);

  const resolveRegionLabels = (codes = []) => {
    if (!codes.length) return [];
    const labels = [];
    let currentLevel = regions;
    codes.forEach(code => {
      if (!currentLevel) return;
      const match = currentLevel.find(item => item.value === code);
      if (match) {
        labels.push(match.label);
        currentLevel = match.children || [];
      }
    });
    return labels;
  };

  useEffect(() => {
    if (!requireAuth(navigate)) return;
    loadData();
    loadRegions();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadCart(), loadAddresses()]);
    } finally {
      setLoading(false);
    }
  };

  const loadRegions = async () => {
    setRegionsLoading(true);
    try {
      const res = await regionsAPI.getRegions();
      setRegions(res.data);
    } catch (error) {
      message.error('加载地区数据失败');
    } finally {
      setRegionsLoading(false);
    }
  };

  const loadCart = async () => {
    try {
      const res = await cartAPI.getCart();
      if (res.data.items.length === 0) {
        message.warning('购物车为空');
        navigate('/cart');
        return;
      }
      setCartItems(res.data.items);
    } catch (error) {
      message.error('加载购物车失败');
    }
  };

  const loadAddresses = async () => {
    try {
      const res = await addressAPI.getAddresses();
      setAddresses(res.data);
      const defaultAddr = res.data.find(addr => addr.is_default);
      if (defaultAddr) {
        setSelectedAddress(defaultAddr.id);
      } else if (res.data.length > 0) {
        setSelectedAddress(res.data[0].id);
      }
    } catch (error) {
      console.error('加载地址失败');
    }
  };

  const handleAddAddress = async (values) => {
    try {
      const [provinceCode, cityCode, districtCode] = values.region || [];
      const regionLabels = selectedRegionOptions.length
        ? selectedRegionOptions.map(option => option?.label)
        : resolveRegionLabels(values.region);
      const [provinceName, cityName, districtName] = regionLabels;

      await addressAPI.addAddress({
        receiver_name: values.receiver_name,
        receiver_phone: values.receiver_phone,
        detail: values.detail,
        province: provinceName || provinceCode,
        city: cityName || cityCode,
        district: districtName || districtCode,
        is_default: addresses.length === 0 ? 1 : 0
      });
      message.success('地址添加成功');
      setShowAddressModal(false);
      form.resetFields();
      setSelectedRegionOptions([]);
      loadAddresses();
    } catch (error) {
      message.error(error.message || '添加地址失败');
    }
  };

  const handleSubmitOrder = async () => {
    if (!selectedAddress) {
      message.warning('请选择收货地址');
      return;
    }

    setSubmitting(true);
    try {
      const res = await orderAPI.createOrder({
        address_id: selectedAddress,
        cart_item_ids: cartItems.map(item => item.id)
      });
      message.success('订单创建成功！');
      window.dispatchEvent(new Event('cartUpdate'));
      navigate(`/orders/${res.data.order_id}`);
    } catch (error) {
      message.error(error.message || '订单创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  const calculateTotal = () => {
    return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2);
  };

  if (loading) {
    return <div className="loading-container">加载中...</div>;
  }

  return (
    <div className="checkout-container">
      <h1 className="page-title">确认订单</h1>

      <div className="checkout-content">
        <div className="checkout-main">
          {/* 收货地址选择 - EDI审核关键！ */}
          <Card title="收货地址" className="section-card">
            {addresses.length === 0 ? (
              <Empty
                description="还没有收货地址"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setShowAddressModal(true)}
                >
                  添加收货地址
                </Button>
              </Empty>
            ) : (
              <>
                <Radio.Group
                  value={selectedAddress}
                  onChange={(e) => setSelectedAddress(e.target.value)}
                  className="address-list"
                >
                  {addresses.map(addr => (
                    <Radio key={addr.id} value={addr.id} className="address-item">
                      <div className="address-content">
                        <div className="address-header">
                          <span className="receiver-name">{addr.receiver_name}</span>
                          <span className="receiver-phone">{addr.receiver_phone}</span>
                          {addr.is_default === 1 && (
                            <span className="default-tag">默认</span>
                          )}
                        </div>
                        <div className="address-detail">
                          {addr.province} {addr.city} {addr.district} {addr.detail}
                        </div>
                      </div>
                    </Radio>
                  ))}
                </Radio.Group>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => setShowAddressModal(true)}
                  style={{ marginTop: 16 }}
                >
                  添加新地址
                </Button>
              </>
            )}
          </Card>

          {/* 商品清单 */}
          <Card title="商品清单" className="section-card">
            <div className="product-list">
              {cartItems.map(item => (
                <div key={item.id} className="product-item">
                  <img
                    src={item.main_image || '/images/default-product.jpg'}
                    alt={item.name}
                    onError={(e) => { e.target.src = '/images/default-product.jpg'; }}
                  />
                  <div className="product-info">
                    <div className="product-name">{item.name}</div>
                    <div className="product-meta">
                      <span>¥{item.price}</span>
                      <span>x {item.quantity}</span>
                    </div>
                  </div>
                  <div className="product-subtotal">
                    ¥{(item.price * item.quantity).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* 订单摘要 */}
        <div className="checkout-sidebar">
          <Card title="订单摘要" className="summary-card">
            <div className="summary-item">
              <span>商品件数:</span>
              <span>{cartItems.length} 件</span>
            </div>
            <div className="summary-item">
              <span>商品总数:</span>
              <span>{cartItems.reduce((sum, item) => sum + item.quantity, 0)} 个</span>
            </div>
            <Divider />
            <div className="summary-total">
              <span>合计:</span>
              <span className="total-price">¥{calculateTotal()}</span>
            </div>
            <Button
              type="primary"
              size="large"
              block
              onClick={handleSubmitOrder}
              loading={submitting}
              disabled={!selectedAddress}
            >
              提交订单
            </Button>
          </Card>
        </div>
      </div>

      {/* 添加地址弹窗 - EDI审核关键！收货信息采集 */}
      <Modal
        title="添加收货地址"
        open={showAddressModal}
        onCancel={() => {
          setShowAddressModal(false);
          form.resetFields();
          setSelectedRegionOptions([]);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddAddress}
        >
          <Form.Item
            label="收货人姓名"
            name="receiver_name"
            rules={[{ required: true, message: '请输入收货人姓名' }]}
          >
            <Input placeholder="请输入收货人姓名" />
          </Form.Item>

          <Form.Item
            label="联系电话"
            name="receiver_phone"
            rules={[
              { required: true, message: '请输入联系电话' },
              { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号' }
            ]}
          >
            <Input placeholder="请输入11位手机号" />
          </Form.Item>

          <Form.Item
            label="所在地区"
            name="region"
            rules={[{ required: true, message: '请选择所在地区' }]}
          >
            <Cascader
              options={regions}
              placeholder="请选择省 / 市 / 区"
              loading={regionsLoading}
              allowClear
              showSearch={{ matchInputWidth: false }}
              onChange={(_, selectedOptions) => setSelectedRegionOptions(selectedOptions)}
              fieldNames={{ label: 'label', value: 'value', children: 'children' }}
            />
          </Form.Item>

          <Form.Item
            label="详细地址"
            name="detail"
            rules={[{ required: true, message: '请输入详细地址' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="请输入详细地址，如街道、门牌号、小区、楼栋号等"
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              保存地址
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Checkout;
